import React from 'react';
import { X, Camera } from 'lucide-react';
import { PetProfile, PetType } from '../types';
import { useTranslation } from '../hooks/useTranslation';

interface PetEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  petForm: Partial<PetProfile>;
  setPetForm: (form: Partial<PetProfile>) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSaving: boolean;
  petsLength: number;
  currentPetId: string | null;
  onLogout: () => void;
}

export const PetEditModal: React.FC<PetEditModalProps> = ({
  isOpen,
  onClose,
  petForm,
  setPetForm,
  onSubmit,
  isSaving,
  petsLength,
  currentPetId,
  onLogout
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-end backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md mx-auto p-8 rounded-t-[3rem] animate-slide-up relative shadow-2xl h-[85vh] flex flex-col">
        <h3 className="text-2xl font-black mb-6">
          {(petForm as any).id ? t('common:buttons.edit') : t('app:pet.add_pet')}
        </h3>
        <button 
          onClick={() => { 
            onClose(); 
            if(petsLength===0 && !currentPetId) onLogout(); 
          }} 
          className="absolute top-8 right-8 text-gray-300"
        >
          <X />
        </button>
        
        <form onSubmit={onSubmit} className="space-y-4 flex-1 overflow-y-auto hide-scrollbar pb-6">
          <div className="flex justify-center mb-6">
            <label className={`relative w-32 h-32 rounded-[2rem] flex items-center justify-center overflow-hidden border-4 border-white shadow-lg cursor-pointer ${petForm.imageUrl ? '' : 'bg-gray-100'}`}>
              {petForm.imageUrl ? (
                <img src={petForm.imageUrl} className="w-full h-full object-cover"/>
              ) : (
                <div className="text-gray-300 flex flex-col items-center">
                  <Camera size={32} />
                  <span className="text-[10px] font-bold mt-1">{t('app:pet.photo')}</span>
                </div>
              )}
              <input 
                type="file" 
                accept="image/*" 
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const MAX = 500;
                        let w = img.width, h = img.height;
                        if (w > h) { if (w > MAX) { h *= MAX/w; w = MAX; } } else { if (h > MAX) { w *= MAX/h; h = MAX; } }
                        canvas.width = w; canvas.height = h;
                        ctx?.drawImage(img, 0, 0, w, h);
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                        setPetForm({ ...petForm, imageUrl: dataUrl });
                      };
                      img.src = event.target?.result as string;
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.type')}</label>
              <select 
                required 
                value={petForm.type} 
                onChange={e => setPetForm({...petForm, type: e.target.value as PetType})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none"
              >
                <option value="chien">{t('app:pet.dog')}</option>
                <option value="chat">{t('app:pet.cat')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.name')}</label>
              <input 
                required 
                value={petForm.name} 
                onChange={e => setPetForm({...petForm, name: e.target.value})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
                placeholder="Rex" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.breed')}</label>
              <input 
                value={petForm.breed} 
                onChange={e => setPetForm({...petForm, breed: e.target.value})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
                placeholder="Ex: Labrador" 
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.color')}</label>
              <input 
                value={petForm.color} 
                onChange={e => setPetForm({...petForm, color: e.target.value})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
                placeholder="Noir" 
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.identification_number')}</label>
            <input 
              value={petForm.identificationNumber || ''} 
              onChange={e => setPetForm({...petForm, identificationNumber: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
              placeholder="" 
            />
          </div>
          
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400">{t('app:pet.birth_date')}</label>
            <input 
              type="date" 
              value={petForm.birthDate} 
              onChange={e => setPetForm({...petForm, birthDate: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
            />
          </div>

          <button 
            disabled={isSaving} 
            type="submit" 
            className="w-full bg-indigo-600 text-white py-4 rounded-[1.5rem] font-black shadow-xl mt-4"
          >
            {t('common:buttons.save')}
          </button>
        </form>
      </div>
    </div>
  );
};
